import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import islrService from '../services/islrService';
import { useCompany } from './CompanyContext';

/**
 * ------------------------------------------------------------------
 * ISLR CONTEXT
 * ------------------------------------------------------------------
 * Maneja el estado global de Retenciones ISLR
 * Proporciona funciones CRUD con caching para optimizar API calls
 * ------------------------------------------------------------------
 */

const ISLRContext = createContext();

export function useISLRContext() {
    const context = useContext(ISLRContext);
    if (!context) {
        throw new Error('useISLRContext debe usarse dentro de un ISLRProvider');
    }
    return context;
}

export function ISLRProvider({ children }) {
    const [retenciones, setRetenciones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { company } = useCompany();

    /**
     * Cargar todas las retenciones ISLR
     * @param {boolean} force - Forzar recarga desde API
     */
    const fetchRetenciones = useCallback(async (force = false) => {
        if (!company || !company.rif) {
            console.warn('No hay empresa seleccionada para cargar retenciones ISLR');
            setRetenciones([]);
            return;
        }

        // Cache hit
        if (!force && retenciones.length > 0) {
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const data = await islrService.getAll({ empresa_rif: company.rif });
            const normalizedData = Array.isArray(data) ? data : (data.data || []);
            setRetenciones(normalizedData);
        } catch (err) {
            console.error('Error fetching retenciones ISLR:', err);
            setError(err.message || 'Error al cargar retenciones ISLR');
        } finally {
            setLoading(false);
        }
    }, [company, retenciones.length]);

    /**
     * Crear una nueva retención ISLR
     */
    const createRetencion = useCallback(async (retencionData) => {
        if (!company || !company.rif) {
            throw new Error('No hay empresa seleccionada');
        }

        setLoading(true);
        try {
            // Agregar empresa_rif automáticamente
            const dataWithEmpresa = {
                ...retencionData,
                empresa_rif: company.rif
            };

            const result = await islrService.createRetencion(dataWithEmpresa);
            // Al crear, forzamos recarga para consistencia
            await fetchRetenciones(true);
            return result;
        } catch (err) {
            console.error('Error creating retencion:', err);
            // Emitimos alerta aquí o lanzamos error?
            // El hook original lanzaba alerta, pero lo ideal es lanzar y que el consumidor maneje UI o tengamos un manejador global.
            // Para compatibilidad con useISLR original, mejor lanzamos y que useISLR o el componente capture.
            throw err;
        } finally {
            setLoading(false);
        }
    }, [fetchRetenciones]);

    /**
     * Eliminar una retención ISLR
     */
    const deleteRetencion = useCallback(async (id) => {
        setLoading(true);
        try {
            await islrService.delete(id, company.rif);
            await fetchRetenciones(true);
            return true;
        } catch (err) {
            console.error('Error deleting retencion:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [company, fetchRetenciones]);

    // Obtener PDF (Stateless, no afecta cache pero lo incluimos por conveniencia)
    const getComprobantePdf = useCallback(async (id) => {
        try {
            return await islrService.getPdf(id, company.rif);
        } catch (err) {
            console.error('Error downloading PDF:', err);
            throw err;
        }
    }, [company]);


    // Limpiar caché cuando cambia la empresa
    useEffect(() => {
        setRetenciones([]);
        // Opcional: fetchRetenciones() si queremos carga eager al cambiar empresa
    }, [company?.rif]);

    const value = {
        retenciones,
        loading,
        error,
        fetchRetenciones,
        createRetencion,
        deleteRetencion,
        getComprobantePdf
    };

    return (
        <ISLRContext.Provider value={value}>
            {children}
        </ISLRContext.Provider>
    );
}
